import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useActiveMeeting } from "../context/ActiveMeetingContext";
import { useMeetings } from "../context/MeetingsContext";
import { FaVideo, FaCopy, FaCheck, FaCalendarPlus, FaLink, FaBolt, FaUsers, FaDesktop, FaComments, FaMicrophone, FaVideoSlash, FaUserPlus, FaTimes, FaCheckCircle, FaExpand, FaCompress } from "react-icons/fa";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import toast from "react-hot-toast";
import { MdCallEnd } from "react-icons/md";
import styles from "./PrrepXMeet.module.css";

/* 笏笏 helpers 笏笏 */
const sanitizeRoom = (name) => name.replace(/[^a-zA-Z0-9_-]/g, "").trim();

const generateRoomId = () => {
  // Use IST for room ID timestamp
  const now = new Date();
  const istStr = now.toLocaleString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false });
  // istStr = "DD/MM/YYYY, HH:mm:ss"
  const [datePart, timePart] = istStr.split(', ');
  const [dd, mm, yyyy] = datePart.split('/');
  const [hh, min, ss] = timePart.split(':');
  return `prrepx-meet-${yyyy}${mm}${dd}-${hh}${min}${ss}`;
};

/* Portal-branded invite link — uses current domain automatically (dev + prod) */
const portalMeetLink = (room) =>
  `${window.location.origin}/dashboard/meet?room=${encodeURIComponent(room)}`;

const buildGoogleCalendarUrl = ({ title, room, details, startISO, endISO, guests = [] }) => {
  const base = "https://calendar.google.com/calendar/render?action=TEMPLATE";
  const inviteLink = portalMeetLink(room);
  const params = new URLSearchParams({
    text: title,
    details: `${details}\n\nJoin PrrepX Meet: ${inviteLink}`,
    location: inviteLink,
    dates: `${startISO.replace(/[-:.]/g, "").slice(0, 15)}Z/${endISO.replace(/[-:.]/g, "").slice(0, 15)}Z`,
    ctz: "Asia/Kolkata",
  });
  guests.forEach(g => { if (g.email) params.append("add", g.email); });
  return `${base}&${params.toString()}`;
};

const toDatetimeLocal = (d) => {
  // Convert to IST string for datetime-local input
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find(p => p.type === type)?.value || '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

const parseDatetimeLocalAsIST = (value) => {
  if (!value) return null;
  const normalized = String(value).replace(' ', 'T').slice(0, 16);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) return null;
  const date = new Date(`${normalized}:00+05:30`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/* 笏笏 component 笏笏 */
const PrrepXMeet = () => {
  const { userData } = useAuth();
  const { addMeeting } = useMeetings();
  const displayName = userData?.name || userData?.email?.split("@")[0] || "Employee";
  const role = userData?.role || "employee";

  /* lobby state */
  const [tab, setTab] = useState("start"); // "start" | "schedule"
  const [roomInput, setRoomInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [generatedRoom, setGeneratedRoom] = useState(() => generateRoomId());

  /* schedule form */
  const now = new Date();
  const later = new Date(now.getTime() + 60 * 60 * 1000);
  const [schedTitle, setSchedTitle] = useState("PrrepX Team Meeting");
  const [schedStart, setSchedStart] = useState(toDatetimeLocal(now));
  const [schedEnd, setSchedEnd] = useState(toDatetimeLocal(later));
  const [schedRoom, setSchedRoom] = useState(() => generateRoomId());
  const [schedDesc, setSchedDesc] = useState("Internal meeting via PrrepX Meet.");
  const [schedLobby, setSchedLobby] = useState(false); // lobby/waiting-room toggle

  /* active meeting state — lifted to ActiveMeetingContext so the Jitsi
     session persists across navigation (PiP mode when browsing other pages) */
  const { activeMeeting, setActiveMeeting } = useActiveMeeting();
  const [tokenLoading, setTokenLoading] = useState(null); // null | 'start' | 'join' | 'schedule'
  const [schedSaving, setSchedSaving] = useState(false);

  // Guest picker state
  const [allUsers, setAllUsers] = useState([]);
  const [schedGuests, setSchedGuests] = useState([]);
  const [guestSearch, setGuestSearch] = useState("");
  const [guestDropOpen, setGuestDropOpen] = useState(false);

  const inputRef = useRef(null);
  const guestSearchRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  /* Fetch portal users for guest picker */
  useEffect(() => {
    getDocs(collection(db, "users"))
      .then(snap => setAllUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() }))))
      .catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Close guest dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (guestSearchRef.current && !guestSearchRef.current.contains(e.target)) {
        setGuestDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Fetch a fresh JaaS JWT from the Cloud Function.
     isOrganizer=true  → employee who STARTS the meeting gets moderator rights.
     isOrganizer=false → employee joining via invite link gets participant rights.
     admins/superadmins are always moderators regardless (set server-side). */
  const fetchJaaSToken = async (isOrganizer = false) => {
    try {
      const fns = getFunctions(app);
      const generateJaaSToken = httpsCallable(fns, "generateJaaSToken");
      const result = await generateJaaSToken({
        displayName,
        email: userData?.email || "",
        photoURL: userData?.photoURL || "",
        isOrganizer,
      });
      return result.data.token;
    } catch (err) {
      console.error("JaaS token error:", err);
      toast.error("Could not start meeting. Please try again.");
      return null;
    }
  };

  /* Auto-join when opened via a shared portal link (?room=...).
     Also re-runs when location.search changes so that clicking "Join" from
     the calendar panel (which navigates to ?room=... on the same page) works
     without requiring a manual refresh. */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roomParam = params.get("room");
    const lobbyParam = params.get("lobby") === "1";
    if (roomParam && !activeMeeting) {
      const safe = sanitizeRoom(roomParam);
      if (safe) {
        launchMeeting(safe, { isOrganizer: false, lobbyEnabled: lobbyParam, loadingKey: 'join' });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /* actions */
  // isOrganizer: true when the current user is starting/owns the meeting.
  // lobbyEnabled: whether guests must wait until a moderator admits them.
  const launchMeeting = async (room, { isOrganizer = false, lobbyEnabled = false, loadingKey = 'start' } = {}) => {
    const safe = sanitizeRoom(room);
    if (!safe) return;

    // Request camera + mic permissions from the TOP-LEVEL frame BEFORE the iframe loads.
    // On mobile, cross-origin iframes cannot trigger the browser permission dialog on their own,
    // so this pre-request is the only reliable way to get the native allow/deny prompt to appear.
    // We immediately stop the stream — we just need the permission grant; Jitsi opens its own stream.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach(t => t.stop());
    } catch (permErr) {
      // User denied or device unavailable — warn but don't block the meeting
      if (permErr.name === "NotAllowedError" || permErr.name === "PermissionDeniedError") {
        toast.error(
          "Microphone/Camera access denied. Please allow permissions in your browser settings, then rejoin.",
          { duration: 6000 }
        );
        // Still allow joining (they might have audio-only use case or will fix via settings)
      }
      // NotFoundError = no device, NotReadableError = device in use — let Jitsi handle those
    }

    // Always fetch a fresh token so isOrganizer is encoded correctly.
    // (Don't reuse cached token — different rooms / roles need different tokens)
    setTokenLoading(loadingKey);
    const token = await fetchJaaSToken(isOrganizer);
    setTokenLoading(null);
    if (!token) return;
    // Sync browser URL so the room link is shareable / bookmarkable
    navigate(`/dashboard/meet?room=${encodeURIComponent(safe)}`, { replace: true });
    setActiveMeeting({ room: safe, token, lobbyEnabled });
  };

  const handleCopy = (text, setter) => {
    navigator.clipboard.writeText(text).then(() => {
      setter(true);
      setTimeout(() => setter(false), 2000);
    });
  };

  /* guest picker helpers */
  const filteredUsers = allUsers.filter(u =>
    u.uid !== userData?.uid &&
    !schedGuests.some(g => g.uid === u.uid) &&
    (!guestSearch.trim() ||
      u.name?.toLowerCase().includes(guestSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(guestSearch.toLowerCase()))
  );

  const addGuest = (user) => {
    setSchedGuests(prev => [...prev, { uid: user.uid, name: user.name || user.email, email: user.email, role: user.role, photoURL: user.photoURL || "" }]);
    setGuestSearch("");
    setGuestDropOpen(false);
  };

  const removeGuest = (uid) => setSchedGuests(prev => prev.filter(g => g.uid !== uid));

  /* Schedule meeting — saves to Firestore, shows rich toast with actions */
  const handleScheduleMeeting = async () => {
    setSchedSaving(true);
    try {
      const start  = parseDatetimeLocalAsIST(schedStart);
      const end    = parseDatetimeLocalAsIST(schedEnd);
      if (!start || !end) {
        toast.error("Invalid start/end date. Please select date and time in IST.");
        return;
      }
      if (end <= start) {
        toast.error("End time must be after start time.");
        return;
      }
      const roomId = sanitizeRoom(schedRoom) || generateRoomId();
      const title  = schedTitle || "PrrepX Meet";
      const link   = portalMeetLink(roomId);
      // Snapshot guests BEFORE addMeeting so the closure stays correct after form reset
      const guestsSnapshot = [...schedGuests];
      const descSnapshot   = schedDesc;

      await addMeeting({
        title,
        room: roomId,
        startISO: start.toISOString(),
        endISO: end.toISOString(),
        description: descSnapshot,
        scheduledBy: displayName,
        scheduledByUid: userData?.uid || "",
        guests: guestsSnapshot,
        lobbyEnabled: schedLobby,
      });

      // Build Google Calendar URL (opened when user clicks the button in toast)
      const gcalUrl = buildGoogleCalendarUrl({
        title,
        room: roomId,
        details: descSnapshot,
        startISO: start.toISOString(),
        endISO: end.toISOString(),
        guests: guestsSnapshot,
      });

      // Rich toast notification
      toast.custom((t) => (
        <div style={{
          display: "flex", flexDirection: "column", gap: 10,
          background: "#fff", border: "1px solid #e2e8f0",
          borderRadius: 14, padding: "16px 18px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
          maxWidth: 360, width: "100%",
          opacity: t.visible ? 1 : 0,
          transition: "opacity 0.2s",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22 }}>✅</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: "0.9rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
              <div style={{ fontSize: "0.74rem", color: "#64748b", marginTop: 2 }}>Meeting scheduled{guestsSnapshot.length > 0 ? ` · ${guestsSnapshot.length} guest${guestsSnapshot.length > 1 ? "s" : ""} invited` : ""}</div>
            </div>
            <button onClick={() => toast.dismiss(t.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, lineHeight: 1, padding: 2 }}>✕</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", borderRadius: 8, padding: "8px 10px", border: "1px solid #e2e8f0" }}>
            <span style={{ flex: 1, fontSize: "0.72rem", color: "#1e40af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{link}</span>
            <button
              onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied!", { duration: 1800 }); }}
              style={{ flexShrink: 0, border: "1px solid #bfdbfe", borderRadius: 6, background: "#eff6ff", color: "#2563eb", padding: "4px 10px", fontSize: "0.71rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >Copy</button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <a
              href={link} target="_blank" rel="noopener noreferrer"
              style={{ flex: 1, textAlign: "center", padding: "7px 0", background: "#2563eb", color: "#fff", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700, textDecoration: "none" }}
              onClick={() => toast.dismiss(t.id)}
            >Join Now</a>
            <button
              onClick={() => { window.open(gcalUrl, "_blank", "noopener,noreferrer"); toast.dismiss(t.id); }}
              style={{ flex: 1, padding: "7px 0", border: "1px solid #e2e8f0", background: "#fff", color: "#475569", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}
            >+ Google Cal</button>
          </div>
        </div>
      ), { duration: 12000, position: "top-right" });

      // Reset form
      setSchedGuests([]);
      setGuestSearch("");
      setSchedLobby(false);
    } finally {
      setSchedSaving(false);
    }
  };



  /* The active meeting is rendered by JitsiPortal which overlays this lobby
     in full mode (/dashboard/meet), or floats as a PiP corner widget when
     navigating to any other route. This lobby is always rendered behind it. */
  /* When meeting is active, render a plain black screen — the portal covers
     everything so there's nothing useful to show, and this avoids the lobby
     hero background bleeding through any sub-pixel gap between navbar and portal. */
  if (activeMeeting) {
    return <div style={{ background: '#000', width: '100%', height: '100%', minHeight: '100vh' }} />;
  }
  /* 笏笏 LOBBY VIEW 笏笏 */
  return (
    <div className={styles.lobbyPage}>

      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroGlow1} />
        <div className={styles.heroGlow2} />
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <FaVideo size={11} /> Internal Video Conferencing
          </div>
          <h1 className={styles.heroTitle}>
            <span className={styles.heroBlue}>PrrepX</span> Meet
          </h1>
          <p className={styles.heroSub}>
            Professional video meetings for the entire team &mdash; HD video, screen sharing, chat, recordings. Free, always.
          </p>
          <div className={styles.heroFeatures}>
            <span><FaDesktop size={12} /> Screen Share</span>
            <span><FaComments size={12} /> In-meeting Chat</span>
            <span><FaMicrophone size={12} /> HD Audio</span>
            <span><FaUsers size={12} /> Unlimited Participants</span>
          </div>
        </div>
        <div className={styles.heroUser}>
          <div className={styles.heroAvatar}>{displayName.charAt(0).toUpperCase()}</div>
          <div>
            <div className={styles.heroUserName}>{displayName}</div>
            <div className={styles.heroUserRole}>{role}</div>
          </div>
          </div>
      </div>
      {/* Tabs */}
      <div className={styles.tabBar}>
        {[
          { key: "start",    icon: <FaVideo size={13} />,       label: "Instant or Join" },
          { key: "schedule", icon: <FaCalendarPlus size={13} />, label: "Schedule" },
        ].map(t => (
          <button
            key={t.key}
            className={`${styles.tabBtn} ${tab === t.key ? styles.tabBtnActive : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      <div className={styles.tabPanel}>

        {/* ── Combined Start / Join panel ── */}
        {tab === "start" && (
          <div className={styles.startPanel}>

            {/* Card: New Meeting */}
            <div className={styles.startCard}>
              <div className={styles.startCardHeader}>
                <div className={styles.startCardIcon}>
                  <FaBolt size={20} />
                </div>
                <div>
                  <h2 className={styles.startCardTitle}>New Instant Meeting</h2>
                  <p className={styles.startCardSub}>Start a fresh room right now</p>
                </div>
              </div>

              <div className={styles.roomPreview}>
                <div className={styles.roomPreviewLabel}>Your room</div>
                <div className={styles.roomPreviewName}>{generatedRoom}</div>
                <button
                  className={styles.regenBtn}
                  onClick={() => setGeneratedRoom(generateRoomId())}
                  title="Generate new room ID"
                >↺ New</button>
              </div>

              <div className={styles.startCardActions}>
                <button className={styles.bigBtn} onClick={() => launchMeeting(generatedRoom, { isOrganizer: true, lobbyEnabled: false, loadingKey: 'start' })} disabled={tokenLoading === 'start'}>
                  <FaVideo size={14} /> {tokenLoading === 'start' ? "Connecting…" : "Start Meeting"}
                </button>
                <button
                  className={styles.bigBtnOutline}
                  onClick={() => handleCopy(portalMeetLink(generatedRoom), setCopied)}
                >
                  {copied ? <FaCheck size={13} /> : <FaCopy size={13} />}
                  {copied ? "Copied!" : "Copy Invite Link"}
                </button>
              </div>
              <p className={styles.startCardHint}>
                Share the invite link &mdash; teammates join from any browser, no downloads needed.
              </p>
            </div>

            {/* Divider */}
            <div className={styles.startDivider}>
              <div className={styles.startDividerLine} />
              <span className={styles.startDividerOr}>OR</span>
              <div className={styles.startDividerLine} />
            </div>

            {/* Card: Join with Link */}
            <div className={styles.startCard}>
              <div className={styles.startCardHeader}>
                <div className={`${styles.startCardIcon} ${styles.startCardIconGreen}`}>
                  <FaLink size={17} />
                </div>
                <div>
                  <h2 className={styles.startCardTitle}>Join with a Link</h2>
                  <p className={styles.startCardSub}>Paste an invite link or room name</p>
                </div>
              </div>

              <div className={styles.inputWrapper}>
                <FaLink className={styles.inputIcon} size={13} />
                <input
                  ref={inputRef}
                  className={styles.bigInput}
                  type="text"
                  placeholder="Paste invite link or type room name..."
                  value={roomInput}
                  onChange={e => setRoomInput(e.target.value)}
                  maxLength={200}
                  autoComplete="off"
                  onKeyDown={e => {
                    if (e.key !== "Enter" || !roomInput.trim()) return;
                    let r = roomInput;
                    if (roomInput.includes("?room=")) r = new URLSearchParams(roomInput.split("?")[1]).get("room") || roomInput;
                    else if (roomInput.includes("meet.jit.si/")) r = roomInput.split("meet.jit.si/")[1].split("#")[0];
                    launchMeeting(r, { isOrganizer: false, loadingKey: 'join' });
                  }}
                />
              </div>

              <button
                className={styles.bigBtn}
                style={{ marginTop: 14, width: "100%", justifyContent: "center" }}
                disabled={!roomInput.trim() || tokenLoading === 'join'}
                onClick={() => {
                  let r = roomInput;
                  if (roomInput.includes("?room=")) r = new URLSearchParams(roomInput.split("?")[1]).get("room") || roomInput;
                  else if (roomInput.includes("meet.jit.si/")) r = roomInput.split("meet.jit.si/")[1].split("#")[0];
                  launchMeeting(r, { isOrganizer: false, loadingKey: 'join' });
                }}
              >
                <FaVideo size={14} /> {tokenLoading === 'join' ? "Connecting…" : "Join Now"}
              </button>
              <p className={styles.startCardHint}>
                Works with PrrepX invite links, room names, or any Jitsi room URL.
              </p>
            </div>

          </div>
        )}

        {/* 笏笏 Schedule 笏笏 */}
        {tab === "schedule" && (
          <div className={styles.schedulePanel}>
            <div className={styles.panelIcon}><FaCalendarPlus size={28} /></div>
            <h2 className={styles.panelTitle}>Schedule a meeting</h2>
            <p className={styles.panelDesc}>
              Fill in the details below. We'll generate a permanent room link and open Google Calendar with everything pre-filled 窶・completely free.
            </p>

            <div className={styles.schedForm}>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Meeting Title</label>
                <input
                  className={styles.formInput}
                  value={schedTitle}
                  onChange={e => setSchedTitle(e.target.value)}
                  placeholder="e.g. Weekly Standup"
                  maxLength={100}
                />
              </div>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Room Name</label>
                <div className={styles.roomInputRow}>
                  <input
                    className={styles.formInput}
                    value={schedRoom}
                    onChange={e => setSchedRoom(sanitizeRoom(e.target.value))}
                    placeholder="unique-room-name"
                    maxLength={60}
                  />
                  <button
                    className={styles.regenBtnSm}
                    onClick={() => setSchedRoom(generateRoomId())}
                    type="button"
                    title="Generate room name"
                  >↺</button>
                </div>
                <span className={styles.formHint}>
                  Invite link: <strong>{window.location.origin}/dashboard/meet?room={schedRoom || "…"}</strong>
                </span>
              </div>
              <div className={styles.formRowDouble}>
                <div className={styles.formRow}>
                  <label className={styles.formLabel}>Start</label>
                  <input className={styles.formInput} type="datetime-local" value={schedStart} onChange={e => setSchedStart(e.target.value)} />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.formLabel}>End</label>
                  <input className={styles.formInput} type="datetime-local" value={schedEnd} onChange={e => setSchedEnd(e.target.value)} />
                </div>
              </div>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Description</label>
                <textarea
                  className={styles.formTextarea}
                  value={schedDesc}
                  onChange={e => setSchedDesc(e.target.value)}
                  rows={3}
                  placeholder="Agenda, notes, etc."
                  maxLength={500}
                />
              </div>

              {/* ── Guest picker ── */}
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Guests</label>

                {/* Selected guest chips */}
                {schedGuests.length > 0 && (
                  <div className={styles.guestChips}>
                    {schedGuests.map(g => (
                      <span key={g.uid} className={styles.guestChip}>
                        <span className={styles.chipAvatar}>{(g.name || g.email || "?")[0].toUpperCase()}</span>
                        <span className={styles.chipName}>{g.name || g.email}</span>
                        <span className={styles.chipRole}>{g.role}</span>
                        <button className={styles.chipRemove} type="button" onClick={() => removeGuest(g.uid)} aria-label="Remove guest">
                          <FaTimes size={9} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add Guests button — expands into search panel */}
                {!guestDropOpen ? (
                  <button
                    type="button"
                    className={styles.addGuestBtn}
                    onClick={() => { setGuestDropOpen(true); setTimeout(() => guestSearchRef.current?.querySelector("input")?.focus(), 50); }}
                  >
                    <FaUserPlus size={13} />
                    {schedGuests.length > 0 ? "Add more guests" : "Add guests"}
                  </button>
                ) : (
                  <div className={styles.guestSearchWrap} ref={guestSearchRef}>
                    <div className={styles.guestInputRow}>
                      <FaUserPlus size={13} className={styles.guestIcon} />
                      <input
                        className={styles.guestInput}
                        placeholder="Search employees by name or email…"
                        value={guestSearch}
                        onChange={e => setGuestSearch(e.target.value)}
                        autoComplete="off"
                        autoFocus
                      />
                      <button
                        type="button"
                        className={styles.guestCloseBtn}
                        onClick={() => { setGuestDropOpen(false); setGuestSearch(""); }}
                        aria-label="Close guest search"
                      >
                        <FaTimes size={11} />
                      </button>
                    </div>

                    {filteredUsers.length > 0 && (
                      <ul className={styles.guestDropdown}>
                        {filteredUsers.slice(0, 8).map(u => (
                          <li key={u.uid} className={styles.guestOption} onClick={() => addGuest(u)}>
                            <div className={styles.guestAvatar}>
                              {u.photoURL
                                ? <img src={u.photoURL} alt="" className={styles.guestAvatarImg} />
                                : (u.name || u.email || "?")[0].toUpperCase()}
                            </div>
                            <div className={styles.guestInfo}>
                              <span className={styles.guestName}>{u.name || "—"}</span>
                              <span className={styles.guestEmail}>{u.email}</span>
                            </div>
                            <span className={`${styles.guestRolePill} ${styles[`role_${u.role}`] || ""}`}>{u.role}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {guestSearch.trim() && filteredUsers.length === 0 && (
                      <div className={styles.guestEmpty}>No matching employees found</div>
                    )}
                  </div>
                )}

                <span className={styles.formHint}>Guests will see this meeting in their portal calendar</span>
              </div>

              {/* ── Lobby / Waiting Room toggle ── */}
              <div className={styles.formRow}>
                <div className={styles.lobbyToggleRow}>
                  <div className={styles.lobbyToggleInfo}>
                    <span className={styles.lobbyToggleLabel}>Enable Waiting Room (Lobby)</span>
                    <span className={styles.lobbyToggleHint}>
                      When on, participants wait until a moderator admits them.
                      Admins &amp; Super Admins bypass the lobby automatically.
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.lobbyToggleBtn} ${schedLobby ? styles.lobbyToggleBtnOn : ""}`}
                    onClick={() => setSchedLobby(v => !v)}
                    aria-pressed={schedLobby}
                    title={schedLobby ? "Disable waiting room" : "Enable waiting room"}
                  >
                    <span className={styles.lobbyToggleThumb} />
                  </button>
                </div>
              </div>
            </div>{/* end schedForm */}

            <div className={styles.schedActions}>
              <button
                className={styles.scheduleBtn}
                onClick={handleScheduleMeeting}
                disabled={!sanitizeRoom(schedRoom) || !schedStart || !schedEnd || schedSaving}
              >
                {schedSaving
                  ? <><span className={styles.btnSpinner} /> Scheduling…</>
                  : <><FaCheckCircle size={16} /> Schedule Now</>}
              </button>
              <button
                className={styles.bigBtnOutline}
                onClick={() => launchMeeting(schedRoom, { isOrganizer: true, lobbyEnabled: schedLobby, loadingKey: 'schedule' })}
                disabled={!sanitizeRoom(schedRoom) || tokenLoading === 'schedule'}
              >
                <FaVideo size={13} /> {tokenLoading === 'schedule' ? 'Connecting…' : 'Launch Now'}
              </button>
            </div>
            <p className={styles.panelHint}>
              "Schedule Now" saves to the portal calendar and automatically opens Google Calendar — no extra steps needed.
            </p>
          </div>
        )}
      </div>

      {/* Feature strip */}
      <div className={styles.featureStrip}>
        {[
          { icon: <FaVideo size={20} />, title: "HD Video", desc: "Crystal-clear video for every call" },
          { icon: <FaDesktop size={20} />, title: "Screen Share", desc: "Share your screen with one click" },
          { icon: <FaComments size={20} />, title: "In-meeting Chat", desc: "Chat alongside your video call" },
          { icon: <FaUsers size={20} />, title: "No Limits", desc: "Unlimited participants, unlimited time" },
          { icon: <FaCalendarPlus size={20} />, title: "Calendar Ready", desc: "Schedule & sync with Google Calendar" },
          { icon: <FaVideoSlash size={20} />, title: "No Account", desc: "Join any meeting without sign-up" },
        ].map(f => (
          <div key={f.title} className={styles.featureCard}>
            <div className={styles.featureIcon}>{f.icon}</div>
            <div className={styles.featureTitle}>{f.title}</div>
            <div className={styles.featureDesc}>{f.desc}</div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default PrrepXMeet;
